import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiClient";
import type {
  CreateImportantDateRequest,
  CreateUserRequest,
  DeleteImportantDateRequest,
  EditImportantDateRequest,
  ImportantDate,
  ImportantDateCategory,
  RoleResponse,
  UpdateUserRequest,
  UserResponse,
  ProfileSearchResponse
} from "@/types/settings";

export const settingsKeys = {
  importantDates: () => ["settings", "important-dates"] as const,
  importantDateCategories: () => ["settings", "important-date-categories"] as const,
  users: () => ["settings", "users"] as const,
  userByEmail: (email: string) => ["settings", "user", email] as const,
  userRoles: () => ["settings", "user-roles"] as const,
  businesses: () => ["settings", "businesses"] as const,
  meetingTypes: () => ["settings", "meeting-types"] as const,
  profileSearch: (searchText: string) => ["settings", "profile-search", searchText] as const
};

export function useImportantDates() {
  return useQuery({
    queryKey: settingsKeys.importantDates(),
    queryFn: () => apiFetch<ImportantDate[]>("ImportantDates/GetAllImportantDates"),
    staleTime: 30_000,
    refetchOnMount: "always"
  });
}

export function useImportantDateCategories() {
  return useQuery({
    queryKey: settingsKeys.importantDateCategories(),
    queryFn: () => apiFetch<ImportantDateCategory[]>("ImportantDates/GetAllImportantDateCategories"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useCreateImportantDate() {
  return useMutation({
    mutationFn: (body: CreateImportantDateRequest) =>
      apiFetch<boolean>("ImportantDates/CreateImportantDate", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useEditImportantDate() {
  return useMutation({
    mutationFn: (body: EditImportantDateRequest) =>
      apiFetch<boolean>("ImportantDates/EditImportantDate", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useDeleteImportantDate() {
  return useMutation({
    mutationFn: (body: DeleteImportantDateRequest) =>
      apiFetch<boolean>("ImportantDates/DeleteImportantDate", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useUsers() {
  return useQuery({
    queryKey: settingsKeys.users(),
    queryFn: () => apiFetch<UserResponse[]>("user/GetAllUsers"),
    staleTime: 30_000,
    refetchOnMount: "always"
  });
}

export function useUserByEmail(email: string) {
  return useQuery({
    queryKey: settingsKeys.userByEmail(email),
    queryFn: () => apiFetch<UserResponse>(`user/${encodeURIComponent(email)}`),
    enabled: Boolean(email),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useUserRoles() {
  return useQuery({
    queryKey: settingsKeys.userRoles(),
    queryFn: () => apiFetch<RoleResponse[]>("user/GetAllUserRoles"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useBusinesses() {
  return useQuery({
    queryKey: settingsKeys.businesses(),
    queryFn: () => apiFetch<string[]>("Business/GetBusinesses"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useMeetingTypes() {
  return useQuery({
    queryKey: settingsKeys.meetingTypes(),
    queryFn: () => apiFetch<string[]>("MeetingType/GetMeetingTypes"),
    staleTime: 5 * 60_000,
    refetchOnMount: "always"
  });
}

export function useProfileSearch(searchText: string) {
  const normalized = searchText.trim();
  return useQuery({
    queryKey: settingsKeys.profileSearch(normalized),
    queryFn: () =>
      apiFetch<ProfileSearchResponse[]>(
        `ProfileSearch/searchDeloitteProfile?searchtext=${encodeURIComponent(normalized)}`
      ),
    enabled: normalized.length >= 2,
    staleTime: 30_000
  });
}

export function useCreateUser() {
  return useMutation({
    mutationFn: (body: CreateUserRequest) =>
      apiFetch<boolean>("user/CreateUser", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useUpdateUser() {
  return useMutation({
    mutationFn: (body: UpdateUserRequest) =>
      apiFetch<boolean>(`user/${encodeURIComponent(body.email)}`, {
        method: "PUT",
        body: JSON.stringify(body)
      })
  });
}

export function useDeleteUser() {
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<boolean>(`user/Delete/${encodeURIComponent(email)}`, {
        method: "POST",
        body: JSON.stringify(email)
      })
  });
}
